import React, { useState, useEffect, memo, useCallback, useMemo } from 'react';
import { useLotteryContract } from '../contracts/hooks';
import {
  ErrorBadge,
  LotteryInfoGrid,
  LotteryColumn as StyledLotteryColumn,
  ColumnTitle,
  StatusBadge,
  DataDisplay,
} from './styled';
import { LotteryDetails, Ticket } from '../contracts/interfaces';
import styled from 'styled-components';

// Define state constants
const LOTTERY_STATES = {
  IDLE: 0,
  ACTIVE: 1,
  DRAWING: 2,
  COMPLETED: 3,
  CANCELLED: 4,
};

const formatTimestampShort = (timestamp: number): string => {
  try {
    const date = new Date(timestamp * 1000);
    const month = date.toLocaleString('en-US', { month: 'short' });
    const day = date.getDate();
    const year = date.getFullYear();
    const time = date.toLocaleString('en-US', { hour: 'numeric', minute: '2-digit' });
    return `${month} ${day}, ${year}, ${time}`;
  } catch {
    return 'Invalid Date';
  }
};

const TicketsContainer = styled.div`
  max-height: 350px;
  overflow-y: auto;
  padding-right: 8px;

  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(0, 208, 192, 0.3);
    border-radius: 2px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: rgba(0, 208, 192, 0.5);
  }
`;

const TicketCard = styled.div<{ $isWinner?: boolean }>`
  padding: 0.875rem;
  margin-bottom: 0.625rem;
  background: ${props => props.$isWinner 
    ? 'linear-gradient(135deg, rgba(251, 191, 36, 0.1) 0%, rgba(245, 158, 11, 0.05) 100%)'
    : props.theme.colors.backgroundLight};
  border: 1px solid ${props => props.$isWinner 
    ? 'rgba(251, 191, 36, 0.3)' 
    : props.theme.colors.glassBorder};
  border-radius: 12px;
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  }

  &:last-child {
    margin-bottom: 0;
  }
`;

const TicketHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.625rem;
`;

const TicketNumber = styled.span`
  font-size: 0.8125rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const WinnerBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.25rem 0.625rem;
  background: linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%);
  color: #000;
  font-size: 0.6875rem;
  font-weight: 700;
  text-transform: uppercase;
  border-radius: 6px;
`;

const NumbersGrid = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
  justify-content: center;
`;

const NumberBall = styled.span<{ $isWinner?: boolean }>`
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  font-size: 0.8125rem;
  font-weight: 600;
  background: ${props => props.$isWinner 
    ? 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)'
    : props.theme.colors.glass};
  color: ${props => props.$isWinner ? '#000' : props.theme.colors.text};
  border: 1px solid ${props => props.$isWinner 
    ? 'transparent' 
    : props.theme.colors.glassBorder};
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem 1rem;
  text-align: center;
  color: ${({ theme }) => theme.colors.textMuted};

  svg {
    width: 48px;
    height: 48px;
    margin-bottom: 1rem;
    opacity: 0.3;
  }

  p {
    font-size: 0.875rem;
    margin: 0;
  }
`;

const InfoValue = styled.span`
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text};
`;

const WinningNumbersContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  justify-content: center;
  margin-top: 0.5rem;
`;

const WinningNumber = styled.span`
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  font-size: 0.875rem;
  font-weight: 700;
  background: linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%);
  color: #000;
  box-shadow: 0 0 12px rgba(251, 191, 36, 0.3);
`;

// Memoize LotteryColumn
const LotteryColumn = memo(StyledLotteryColumn);

// Define props for TicketItem
interface TicketItemProps {
  ticket: Ticket;
  index: number;
}

// Memoize individual Ticket rendering
const TicketItem: React.FC<TicketItemProps> = memo(({ ticket, index }) => (
  <TicketCard $isWinner={ticket.isWinner}>
    <TicketHeader>
      <TicketNumber>Ticket #{index + 1}</TicketNumber>
      {ticket.isWinner && <WinnerBadge>🏆 Winner</WinnerBadge>}
    </TicketHeader>
    <NumbersGrid>
      {ticket.numbers
        .sort((a: number, b: number) => a - b)
        .map((num: number, numIndex: number) => (
          <NumberBall key={numIndex} $isWinner={ticket.isWinner}>
            {num}
          </NumberBall>
        ))}
    </NumbersGrid>
  </TicketCard>
));

// Define props for MemoizedTicketsContainer
interface MemoizedTicketsContainerProps {
  userTickets: Ticket[];
}

// Memoize the TicketsContainer
const MemoizedTicketsContainer: React.FC<MemoizedTicketsContainerProps> = memo(
  ({ userTickets }) => (
    <TicketsContainer>
      {userTickets.length > 0 ? (
        userTickets.map((ticket, index) => (
          <TicketItem key={ticket.id || index} ticket={ticket} index={index} />
        ))
      ) : (
        <EmptyState>
          <p>No tickets purchased in this round yet.</p>
        </EmptyState>
      )}
    </TicketsContainer>
  )
);

interface LotteryInfoProps {
  timeRemaining: number;
  lotteryDetails?: LotteryDetails | null;
}

const LotteryInfo: React.FC<LotteryInfoProps> = ({ timeRemaining, lotteryDetails: lotteryDetailsProp }) => {
  const { getLotteryDetails, getUserTickets } = useLotteryContract();
  const [lotteryDetails, setLotteryDetails] = useState<LotteryDetails | null>(
    lotteryDetailsProp ?? null
  );
  const [userTickets, setUserTickets] = useState<Ticket[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia('(max-width: 480px)').matches;
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mql = window.matchMedia('(max-width: 480px)');
    const handler = () => {
      setIsMobile(mql.matches);
      if (process.env.NODE_ENV === 'development') {
        console.debug('[LotteryInfo] mobile date formatting', { isMobile: mql.matches });
      }
    };

    handler();
    if (mql.addEventListener) mql.addEventListener('change', handler);
    else mql.addListener(handler);

    return () => {
      if (mql.removeEventListener) mql.removeEventListener('change', handler);
      else mql.removeListener(handler);
    };
  }, []);

  // Helper function to get lottery state name
  const getLotteryStateName = useCallback(
    (state: number): string => {
      if (state === LOTTERY_STATES.ACTIVE && timeRemaining < 15) {
        return 'drawing';
      }

      switch (state) {
        case LOTTERY_STATES.IDLE:
          return 'IDLE';
        case LOTTERY_STATES.ACTIVE:
          return 'active';
        case LOTTERY_STATES.DRAWING:
          return 'drawing';
        case LOTTERY_STATES.COMPLETED:
          return 'completed';
        case LOTTERY_STATES.CANCELLED:
          return 'cancelled';
        default:
          return 'unknown';
      }
    },
    [timeRemaining]
  );

  // Sync prop -> state and fetch tickets; avoid refetching details when provided by parent
  useEffect(() => {
    let cancelled = false;
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        // If parent provided details, use them; otherwise fetch
        const details = lotteryDetailsProp ?? (await getLotteryDetails());
        if (!cancelled) setLotteryDetails(details || null);

        const tickets = await getUserTickets();
        if (!cancelled) setUserTickets(tickets || []);

        if (!cancelled) setLoading(false);
      } catch (err) {
        console.error('Error fetching data:', err);
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Unknown error');
          setLoading(false);
        }
      }
    };

    fetchData();

    // Periodic refresh of tickets and (only if no prop) details
    const intervalId = setInterval(async () => {
      try {
        if (!lotteryDetailsProp) {
          const details = await getLotteryDetails();
          if (!cancelled) setLotteryDetails(details || null);
        }
        const tickets = await getUserTickets();
        if (!cancelled) setUserTickets(tickets || []);
      } catch (err) {
        // swallow
      }
    }, 5000);

    return () => {
      cancelled = true;
      clearInterval(intervalId);
    };
  }, [getLotteryDetails, getUserTickets, lotteryDetailsProp]);

  // Render loading or error states only if no existing data
  if (!lotteryDetails && loading) return <div>Loading lottery details...</div>;
  if (!lotteryDetails && error) return <ErrorBadge>{error}</ErrorBadge>;

  return (
    <LotteryInfoGrid>
      {lotteryDetails && (
        <>
          <LotteryColumn>
            <ColumnTitle>Lottery Details</ColumnTitle>
            <DataDisplay>
              <strong>Start Time</strong>
              <InfoValue>
                {isMobile
                  ? formatTimestampShort(lotteryDetails.startTime)
                  : lotteryDetails.formattedStartTime || 'N/A'}
              </InfoValue>
            </DataDisplay>
            <DataDisplay>
              <strong>End Time</strong>
              <InfoValue>
                {isMobile
                  ? formatTimestampShort(lotteryDetails.endTime)
                  : lotteryDetails.formattedEndTime || 'N/A'}
              </InfoValue>
            </DataDisplay>
            <DataDisplay>
              <strong>Current Pool</strong>
              <InfoValue>{lotteryDetails.formattedPool || 'N/A'}</InfoValue>
            </DataDisplay>
            <DataDisplay>
              <strong>Total Pool</strong>
              <InfoValue>{lotteryDetails.formattedTotalPool || 'N/A'}</InfoValue>
            </DataDisplay>
          </LotteryColumn>

          <LotteryColumn>
            <ColumnTitle>Lottery Status</ColumnTitle>
            <StatusBadge $status={getLotteryStateName(lotteryDetails.state)}>
              {getLotteryStateName(lotteryDetails.state)}
            </StatusBadge>
            <DataDisplay>
              <strong>Round</strong>
              <InfoValue>#{lotteryDetails.lotteryId || 'N/A'}</InfoValue>
            </DataDisplay>
            <DataDisplay>
              <strong>Numbers per Ticket</strong>
              <InfoValue>{lotteryDetails.numbers || 'N/A'}</InfoValue>
            </DataDisplay>
            <DataDisplay>
              <strong>Max Number</strong>
              <InfoValue>{lotteryDetails.maxNum}</InfoValue>
            </DataDisplay>
            {lotteryDetails.winningNumbers && lotteryDetails.winningNumbers.length > 0 && (
              <div style={{ marginTop: '1rem' }}>
                <strong style={{ fontSize: '0.8125rem', color: '#9ca3af', display: 'block', marginBottom: '0.5rem' }}>
                  {lotteryDetails.state === LOTTERY_STATES.COMPLETED ? 'Winning Numbers' : 'Previous Winning Numbers'}
                </strong>
                <WinningNumbersContainer>
                  {lotteryDetails.winningNumbers.map((num, index) => (
                    <WinningNumber key={index}>{num}</WinningNumber>
                  ))}
                </WinningNumbersContainer>
              </div>
            )}
          </LotteryColumn>

          <LotteryColumn>
            <ColumnTitle>Your Tickets ({userTickets.length})</ColumnTitle>
            <MemoizedTicketsContainer userTickets={userTickets} />
          </LotteryColumn>
        </>
      )}
    </LotteryInfoGrid>
  );
};

export default LotteryInfo;
